// What the studio file service accepts, and what a refusal says.
//
// ── These numbers are a mirror. functions/files/limits.js is the authority ──
//
// The server holds them and storage.rules enforces them on the bytes; nothing
// here is enforcement. This file exists so a refusal arrives before a large
// file crawls up a phone connection to be turned away at the bucket, which
// only works if the copies agree: a pre-check looser than the rules lets a
// file upload and come back with a permission sentence for a size problem.
// **Change one, change both.**
//
// MMS (#86): 2 GB per file, no per-studio ceiling, and video accepted.
// Retention is described on `lapsedGraceDays` in the server file.
//
// The long justification for each number lives with the authority in
// functions/files/limits.js.
import { BRAND } from '../domain/brand.js';
import { coreSync } from '../domain/core.js';
import { unverifiedRefusal } from '../domain/clientEvents.js';
import { documentFromRows } from '../domain/documents.js';

export const FILE_LIMITS = Object.freeze({
  // Per object, matching MMS's published "as long as each file is under
  // 2GB". storage.rules compares `request.resource.size <= this`, so the
  // pre-check below refuses at strictly greater and the two agree exactly.
  maxObjectBytes: 2 * 1024 * 1024 * 1024,
  // Authorizations per studio per UTC day. Taken in the callable's
  // transaction, spent up front, never refunded — the client only reads the
  // refusal.
  uploadsPerDayPaid: 200,
  uploadsPerDayTrial: 50,
  // Total bytes a studio may hold. `null` is no ceiling, for a paying studio
  // (as MMS's "unlimited"). A trial keeps one, and so does the `unknown` state
  // a failed entitlement read lands in. Trigger-maintained, so a slow bound
  // rather than an exact one.
  heldBytesPaid: null,
  heldBytesTrial: 2 * 1024 * 1024 * 1024,
  // Days after a studio's entitlement lapses before its files are deleted; a
  // file has no expiry of its own. Owned by the server sweep in
  // functions/retention/files.js — a bucket lifecycle rule cannot condition on
  // a date that lives on the user document.
  lapsedGraceDays: 60,
  // Set on the object at upload and normalised by the finalize trigger. There
  // is no purge on this path, so this hour is how long a deleted file keeps
  // being served from the edge.
  cacheControl: 'public, max-age=3600',
});

// The allowlist, in the two syntaxes that need it.
//
// `ACCEPTED_FILE_TYPES` is for a file input's `accept` attribute, which takes
// glob-ish MIME patterns; storage.rules takes an RE2 regular expression. They
// describe the same set and are deliberately spelled differently.
//
// Video is accepted, as in MMS. `text/csv` is spelled out (#118) — a .csv types
// as text/csv, not text/plain — rather than folded into `text/*`, because
// `text/html` is pinned refused in test/functions/fileLimits.test.js.
// functions/files/limits.js surveys the neighbouring types.
//
// The six Office types (#133), because MMS stores them. Listed by type rather
// than by extension so this string and the rules' literal stay one translation
// apart (test/integration/fileRules.test.jsx chains them); a browser that types
// an Office file as `application/octet-stream` is caught by `uploadType`
// below. This attribute is only a picker filter: a teacher can switch the
// chooser to "All Files" and meet the real gate.
export const ACCEPTED_FILE_TYPES = 'image/*,audio/*,video/*,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
// The gate itself is `is_allowed_type` in core/src/files.rs.

// A content type as the rule will see it. Some platforms hand back
// `text/plain;charset=utf-8` for a .txt, and storage.rules' anchored
// `matches('…|text/plain')` would miss it. So the parameters come off here, and
// this is the string sent as the object's contentType, not `file.type` raw.
export function normaliseType(contentType) {
  return coreSync().normaliseFileType(String(contentType || ''));
}

export function isAllowedType(contentType) {
  return coreSync().isAllowedFileType(String(contentType || ''));
}

// The type a File goes up as (#133): the browser's, normalised as above, or —
// when the browser said nothing useful (empty, or `application/octet-stream`,
// as many platforms say for a spreadsheet) — the type its extension names.
// `upload_type` in core/src/files.rs is the rule; it lives on the client
// because storage.rules cannot see a name. Every upload path sends this one
// string to the pre-check, the callable and the object alike.
export function uploadType(file) {
  return coreSync().fileUploadType(String(file?.type ?? ''), String(file?.name ?? ''));
}

// ── A receipt is a file, and takes the file's cap and list (#96) ────────────
//
// `uploadAttachment` in src/data/useExpenses.jsx and `attach` in
// src/hooks/useExpenseEntryForm.js call `refuseFile` below. MMS states no
// attachment size limit, sets no `accept` filter, and takes a PDF or a CSV on
// an expense, so a receipt matches our own file service.
//
// ── The scan limit, which is a different gate ───────────────────────────────
//
// Not a storage limit: the receipt OCR service's own ceiling (MAX_UPLOAD_BYTES
// in services/receipt-ocr/src/server.js, 12 MB by default), and the scan path
// POSTs the file's bytes straight at that service. A file can be too big to
// scan and fine to attach, and the sentence below says so.
//
// MAX_UPLOAD_BYTES is an environment variable on a separately deployed
// service, so a deployment that overrides it makes this mirror wrong. It fails
// in the tolerable direction — the service answers 413 with a sentence of its
// own, which `extractReceipt` surfaces — so this is a hint, not an authority.
export const MAX_SCAN_BYTES = 12 * 1024 * 1024;

// What the Scan a Receipt picker offers, which is not what the store accepts
// (#113, #96). The OCR service reads photographs and PDFs; the attachment
// picker takes `ACCEPTED_FILE_TYPES`. MMS draws the same line: "Add Expense
// From Photo or PDF" above an attachment dropzone that names no format.
//
// Enumerated rather than `image/*` because a phone's chooser is better for the
// enumeration. Nothing is refused by it: a teacher who picks another file gets
// the service's own answer.
export const ACCEPTED_SCAN_TYPES =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,image/tiff,image/bmp,application/pdf';

// Names the other route: the file is too big for the reader and small enough
// for the store.
//
// The number and the sentence are `core/src/expenses.rs`'s (`scan_too_large`),
// so a phone refuses the same file with the same words;
// `test/core/expenses.wasm.test.js` holds the two constants above to the
// core's.
//
// `decimal` is the studio's Region's decimal separator (`useLocale()`'s
// `decimalSeparator`): `13,4 MB` in Germany (#583). Required, as the core's
// is, so no caller writes a point by leaving it out.
export function scanTooLarge(file, decimal) {
  if (!file) return null;
  return coreSync().expenseScanTooLarge(Number(file.size), decimal) ?? null;
}

// ── Which receipt objects nothing points at any more (#94, #104) ────────────
//
// The expense form can leave an object in the bucket that no row names, and
// those bytes count toward `bytesHeld` (#62).
//
// Deleting on the Remove button press would destroy a saved entry's evidence
// if the teacher then cancelled the form. So the decision is made at save —
// and at abandon — by comparing what the entry had against what it has now, as
// `useExpenses.removeEntry` does when an entry goes with its receipt.
//
//   * `uploaded` — every path this form session put in the bucket. The form's
//     attachment list is not a record of them: a receipt removed from the list
//     before save is gone from it entirely.
//   * `savedPaths` — what the entry being edited already points at, empty for
//     an entry never saved. None of them is in `uploaded`.
//   * `keptPaths` — what a row points at once the action is done: the form's
//     list on a save; `savedPaths` on an abandon, so the row's receipts
//     survive.
//
// All three are lists, because an entry holds many receipts: an entry that
// drops one of its two receipts must have exactly that one swept. `paths`
// turns a non-string entry into '', and the core skips empty paths.
//
// The comparison is `core/src/expenses.rs`'s (`orphaned_receipts`), so a
// phone's form sweeps exactly what this one does.
const paths = (list) => (Array.isArray(list) ? list : []).map((p) => (typeof p === 'string' ? p : ''));

export function orphanedReceipts({ uploaded = [], savedPaths = [], keptPaths = [] }) {
  return coreSync().expenseOrphanedReceipts(paths(uploaded), paths(savedPaths), paths(keptPaths));
}

// ── An object the bucket would not take away (#140) ─────────────────────────
//
// Neither delete path throws: `removeAttachment` and `removeEntry` report a
// storage error rather than refuse to finish saving or deleting an expense.
// When the bucket refuses, that leaves an object nothing points at, charged
// against `bytesHeld`.
//
// This console line is the immediate, local report. The durable one is
// `findOrphanedReceipts` in functions/files/reconcile.js, whose weekly pass
// reads the studio's expense rows and logs every receipt object no row names
// in `files.reconcile.orphans`. It sees the other routes to the same state —
// a tab closed mid-form, or an offline `save` whose awaited commit never
// settles — which a record written here could not. Nothing deletes them;
// `findOrphanedReceipts` says why. Both delete paths call this.
export function warnUnsweptReceipts(paths) {
  const unswept = (Array.isArray(paths) ? paths : [paths]).filter(Boolean);
  if (!unswept.length) return unswept;
  console.warn(
    '[expenses] these receipt objects could not be removed and nothing points at them any more. '
    + 'They stay charged against the studio\'s storage and no sweep will find them:',
    unswept,
  );
  return unswept;
}

// ── The sentences ───────────────────────────────────────────────────────────
//
// Each names its own reason (#62). The two the server owns — the daily
// allowance and the byte ceiling — are not written here: they arrive on the
// callable's error carrying the number the studio was measured against, and
// `uploadErrorMessage` passes them through.

// Same shape as receiptOcr.js's own size refusal, so the two read alike:
// name what was offered and name the limit, both in the same unit.
// `decimal` is the studio's Region's decimal separator (#583).
export const tooLarge = (bytes, decimal) => coreSync().fileTooLarge(Number(bytes), decimal);

// A stored file's size as every list of files shows it — the refusal's own
// arithmetic (`file_size` in core/src/files.rs), so the library's Size column
// and the too-large sentence cannot write one number two ways.
export const fileSizeText = (bytes) => coreSync().fileSizeText(Number(bytes) || 0);

// Names what is accepted: what is refused — a .zip, a file the browser could
// not type at all — has no single name a teacher would recognise. Verbatim
// the server's TYPE_REFUSED, which a bypassed pre-check would get.
export const wrongType = () => coreSync().refuseFile(true, 0, '', BRAND.name, '.').message;

// The four sentences other modules throw by name. Literals, because they are
// read at module scope — before the core has loaded in a browser — and
// `test/core/fileSentences.wasm.test.js` holds each to the core's
// `upload_message`, where the phones read them (#472).
//
// NOT_VERIFIED replaces the SDK's "User does not have permission to access
// 'files/<uid>/…'", whose cause is almost never permission: a bypassed
// callable, an expired reservation, or a missing cross-service IAM grant.
export const NOT_VERIFIED = 'That upload could not be verified. Try again.';

// The fallback, for an attachment failure nothing has classified.
export const UPLOAD_FAILED = 'The file could not be uploaded.';

// The bytes landed and the row did not, which is not "could not be uploaded":
// the bucket is holding the file and charging the ceiling.
//
// The callable refuses a lapsed studio before any bytes move, so a refused row
// write means the row's shape or path failed firestore.rules — a bug. Trying
// again works, at the cost of a second authorization and a second copy of the
// bytes.
export const NOT_RECORDED =
  'That file was stored but could not be added to your studio. Try again.';

// Taking one away failed. Separate from the upload sentences because the file
// is still there and still counted.
export const NOT_REMOVED = 'That file could not be removed. Try again.';

// A refusal a teacher can act on, raised before any bytes move. Typed for the
// same reason ReceiptOcrError is: the message is a sentence written for a
// person, so a caller can show it without deciding whether it is safe to.
export class FileRefused extends Error {}

// Size and type, in that order, against a File. Returns the sentence, or null
// if the file may be offered to the callable. `decimal` is the studio's
// Region's decimal separator the too-large sentence writes its sizes with
// (`2,5 GB` in Germany, #583).
export function refuseFile(file, decimal) {
  const size = file?.size;
  const refusal = coreSync().refuseFile(
    Boolean(file), size == null ? null : Number(size), file ? uploadType(file) : '', BRAND.name, decimal,
  );
  return refusal ? refusal.message : null;
}

// ── Retention (#86) ─────────────────────────────────────────────────────────
//
// A file has no expiry while the studio's subscription is open. When the sweep
// in functions/retention/files.js deletes a lapsed studio's objects it stamps
// `removedOn` on each row, and the UI renders that instead of a link; the row
// survives as a tombstone.

// Has this file been deleted? A recorded date, written by the same pass that
// does the deleting, so it cannot be early or late.
export function isRemoved(row) {
  return Boolean(row?.removedOn);
}

// What a teacher reads in place of the link. Names the date and the reason, so
// a file that has gone does not read as a fault, and names the subscription,
// which is the thing they can act on. `datePattern` is the studio's short-date
// pattern (#589).
export const removedOn = (date, datePattern) => coreSync().fileRemovedOn(String(date), String(datePattern ?? ''));

// ── Turning a thrown thing into a sentence ──────────────────────────────────
//
// Serves both file trees: the receipts path throws the same `storage/*` codes
// from the same SDK, and neither shows the SDK's raw messages.
export function uploadErrorMessage(err) {
  if (!err) return UPLOAD_FAILED;
  // Our own pre-check, or anything else that has already written a sentence
  // for a person.
  if (err instanceof FileRefused) return err.message;
  // firebase-functions' own refusal of `authorizeUpload` — App Check, or an
  // ID token that did not verify — is not "sign in again" alone (#493).
  const unverified = unverifiedRefusal(err);
  if (unverified) return unverified;

  const code = String(err.code || '');
  if (code === 'storage/unauthorized') {
    // The most likely cause is not in this codebase: the Cloud Storage service
    // agent's Firestore grant (launch checklist §5b) is offered interactively
    // by `firebase deploy` and skipped silently on a non-interactive one.
    // Without it the rule's cross-service lookup resolves as NOT_FOUND and
    // fails closed, so the callable succeeds and every object write 403s for
    // every studio.
    console.warn(
      '[files] storage refused the write. If this is every upload rather than one, '
      + 'check the cross-service rules grant in docs/launch-checklist.md §5b.',
      err,
    );
  }
  // What each code means to a teacher is the core's (#472,
  // `upload_error_message` in core/src/files.rs), shared with the phones. The
  // callable's own refusal — the daily allowance, the byte ceiling, the lapsed
  // subscription — is passed through, except `functions/internal`, the SDK's
  // word for an unhandled server exception; an unclassified error is the
  // generic sentence, never the SDK's own message.
  return coreSync().uploadErrorMessage(code, typeof err.message === 'string' ? err.message : null);
}

// The `files` row an upload records (useFiles.jsx says why it is this small):
// what was stored, where, and on which day, with `removedOn` left for the
// sweep.
export function fileRowDocument({ name, contentType, size, path, uploadedOn }) {
  return documentFromRows(coreSync().fileRowDocument(
    String(name), String(contentType), Number(size), String(path), String(uploadedOn),
  ));
}
